import type { ReactNode } from 'react';
import { useI18n } from '../i18n/useI18n';
import type { ApiError, ApiErrorCode } from '../api/errors';
import type { SimpleKey } from '../i18n/types';
import { failureKey } from '../screens/account/identityChoice';

export type AlertTone = 'deny' | 'warn' | 'info' | 'neutral';

const GLYPH: Record<AlertTone, string> = {
  deny: '✕',
  warn: '!',
  info: '•',
  neutral: '–',
};

/**
 * `role="alert"` only when something failed — announcing every explanatory box
 * would train people to ignore the announcements that matter.
 */
export function Alert({
  tone,
  title,
  children,
  live = false,
}: {
  tone: AlertTone;
  title?: string;
  children?: ReactNode;
  live?: boolean;
}) {
  return (
    <div className={`alert alert-${tone}`} {...(live ? { role: 'alert' } : {})}>
      <span className="amark" aria-hidden="true">
        {GLYPH[tone]}
      </span>
      <div>
        {title !== undefined && <div className="atitle">{title}</div>}
        {children !== undefined && <div className="muted">{children}</div>}
      </div>
    </div>
  );
}

/**
 * Every refusal the API can answer with, in words.
 *
 * Keyed by the server's own code, so the console never reads a sentence to work
 * out what happened. The two codes no server sends are here too: a request that
 * did not get through, and an answer carrying a code this console has never
 * heard of — both have to say something rather than nothing.
 */
const ERROR_TITLE: Record<ApiErrorCode, SimpleKey> = {
  network: 'error.network.title',
  unexpected: 'error.unexpected.title',
  unauthenticated: 'error.unauthenticated.title',
  bad_credentials: 'error.badCredentials.title',
  too_many_attempts: 'error.tooManyAttempts.title',
  forbidden: 'error.forbidden.title',
  sign_in_unavailable: 'error.signInUnavailable.title',
  sign_in_expired: 'error.signInExpired.title',
  sign_in_refused: 'error.signInRefused.title',
  provider_unreachable: 'error.providerUnreachable.title',
  operator_not_nameable: 'error.notNameable.title',
  no_such_console: 'error.noSuchConsole.title',
  tenant_not_found: 'error.tenantNotFound.title',
  resource_not_found: 'error.resourceNotFound.title',
  account_not_found: 'error.accountNotFound.title',
  invalid_request: 'error.invalidRequest.title',
  provider_identity_unavailable: 'error.providerUnavailable.title',
  identity_provider_refused: 'error.identityProviderRefused.title',
  conflict: 'error.conflict.title',
  change_under_way: 'error.changeUnderWay.title',
  no_failure_to_dismiss: 'error.noFailureToDismiss.title',
  tenant_unreachable: 'error.tenantUnreachable.title',
  cluster_refused: 'error.clusterRefused.title',
  audit_class_unknown: 'error.auditClassUnknown.title',
  internal: 'error.internal.title',
};

const ERROR_BODY: Record<ApiErrorCode, SimpleKey> = {
  network: 'error.network.body',
  unexpected: 'error.unexpected.body',
  unauthenticated: 'error.unauthenticated.body',
  bad_credentials: 'error.badCredentials.body',
  too_many_attempts: 'error.tooManyAttempts.body',
  forbidden: 'error.forbidden.body',
  sign_in_unavailable: 'error.signInUnavailable.body',
  sign_in_expired: 'error.signInExpired.body',
  sign_in_refused: 'error.signInRefused.body',
  provider_unreachable: 'error.providerUnreachable.body',
  operator_not_nameable: 'error.notNameable.body',
  no_such_console: 'error.noSuchConsole.body',
  tenant_not_found: 'error.tenantNotFound.body',
  resource_not_found: 'error.resourceNotFound.body',
  account_not_found: 'error.accountNotFound.body',
  invalid_request: 'error.invalidRequest.body',
  provider_identity_unavailable: 'error.providerUnavailable.body',
  identity_provider_refused: 'error.identityProviderRefused.body',
  conflict: 'error.conflict.body',
  change_under_way: 'error.changeUnderWay.body',
  no_failure_to_dismiss: 'error.noFailureToDismiss.body',
  tenant_unreachable: 'error.tenantUnreachable.body',
  cluster_refused: 'error.clusterRefused.body',
  audit_class_unknown: 'error.auditClassUnknown.body',
  internal: 'error.internal.body',
};

/**
 * The refusals whose own sentence is shown under the words for them.
 *
 * Not every one, and the rule is what the sentence adds. A refusal this console
 * has wording for is described by that wording, and the server's English under
 * it would be the same thing said twice — in one language on a console that
 * shows two. These five carry something the console could not have composed: a
 * code it has never heard of, a storage failure, the address of a provider that
 * went quiet, the date a certificate ran out on — and, for a cluster that
 * refused, the cluster's own reason, which is the thing a person acts on.
 *
 * `cluster_refused` is here because its body promises it in as many words, and
 * a promise of "what it said is below" with nothing below is worse than a body
 * that promised nothing.
 */
const SHOWS_SENTENCE: readonly ApiErrorCode[] = [
  'unexpected',
  'internal',
  'provider_unreachable',
  'identity_provider_refused',
  'cluster_refused',
];

/**
 * The server's own sentence for a refusal, where it is shown at all and where
 * there is one.
 *
 * `ApiError` falls back to the code when the answer carried no sentence, and
 * the bare code under a promise of the server's words is not those words — so
 * that case reads here as no sentence rather than as `cluster_refused` printed
 * at somebody.
 */
export function serverSentence(error: ApiError): string | undefined {
  if (!SHOWS_SENTENCE.includes(error.code)) return undefined;
  return error.message === error.code ? undefined : error.message;
}

/** The words one refusal reads under. Exported so a screen can place them itself. */
export function errorTitleKey(code: ApiErrorCode): SimpleKey {
  return ERROR_TITLE[code];
}

export function errorBodyKey(code: ApiErrorCode): SimpleKey {
  return ERROR_BODY[code];
}

/**
 * Turns an `ApiError` into words. Screens never write failure prose themselves.
 *
 * Three things ride along where they exist. A full window of sign-in attempts
 * says how long it is, when the deployment says so at all — the point of a
 * `429` is the wait, and "error 429" is not that. Which check of an identity
 * provider refused is named in the reader's own language. And the server's own
 * sentence is shown for the refusals `serverSentence` lists, where it carries
 * what the console could not have written itself — the address that went quiet,
 * a code nobody here has heard of, or the cluster's own reason for saying no.
 */
export function ErrorAlert({ error, action }: { error: ApiError; action?: ReactNode }) {
  const { t, formatDuration } = useI18n();
  const wait = error.retryAfterSeconds;
  const unknown = error.unknownCode;
  const sentence = serverSentence(error);

  return (
    <Alert tone="deny" title={t(ERROR_TITLE[error.code])} live>
      <p>{t(ERROR_BODY[error.code])}</p>

      {error.code === 'too_many_attempts' && wait !== undefined && (
        <p style={{ marginTop: 8 }}>
          {t('error.tooManyAttempts.wait', { wait: formatDuration(wait) })}
        </p>
      )}

      {unknown !== undefined && (
        <p className="mono" style={{ marginTop: 8 }}>
          {unknown}
        </p>
      )}

      {/* Which of the provider's answers refused, in the reader's language,
          and then the server's own line under it: the sentence carries the
          address it dialled and the date a certificate ran out on, and neither
          is something this console could compose. */}
      {error.code === 'identity_provider_refused' && (
        <p style={{ marginTop: 8 }}>{t(failureKey(error.failure))}</p>
      )}

      {sentence !== undefined && <p style={{ marginTop: 8 }}>{sentence}</p>}

      {action}
    </Alert>
  );
}
